export interface TextSegment {
  id: number;
  label: string;
  content: string;
  displayText: string;
  tagName: string;
}

/**
 * Strips inline HTML tags from content, preserving only text characters.
 * Does NOT collapse whitespace — maintains 1:1 character mapping with the
 * original HTML text nodes for accurate edit reconstruction.
 */
function stripInlineTags(html: string): string {
  return html.replace(/<[^>]+>/g, '').trim();
}

/**
 * Rebuilds inner HTML by applying text edits while preserving all inline tags.
 *
 * Uses prefix/suffix matching to find the changed region in the display text,
 * then walks through the original HTML character by character — tags are always
 * preserved, and only the text characters in the changed region are replaced.
 */
function rebuildInnerHtml(originalInnerHtml: string, originalDisplay: string, editedDisplay: string): string {
  if (originalDisplay === editedDisplay) return originalInnerHtml;

  // Find common prefix length
  let prefixLen = 0;
  while (
    prefixLen < originalDisplay.length &&
    prefixLen < editedDisplay.length &&
    originalDisplay[prefixLen] === editedDisplay[prefixLen]
  ) {
    prefixLen++;
  }

  // Find common suffix length (not overlapping with prefix)
  let suffixLen = 0;
  const maxSuffix = Math.min(originalDisplay.length - prefixLen, editedDisplay.length - prefixLen);
  while (
    suffixLen < maxSuffix &&
    originalDisplay[originalDisplay.length - 1 - suffixLen] === editedDisplay[editedDisplay.length - 1 - suffixLen]
  ) {
    suffixLen++;
  }

  const changeStart = prefixLen;
  const changeEndOriginal = originalDisplay.length - suffixLen;
  const changedNew = editedDisplay.substring(prefixLen, editedDisplay.length - suffixLen);

  // Walk through original HTML, tracking display text position
  const result: string[] = [];
  let displayPos = 0;
  let i = 0;
  let changeInserted = false;

  while (i < originalInnerHtml.length) {
    if (originalInnerHtml[i] === '<') {
      // HTML tag — always preserve
      const tagEnd = originalInnerHtml.indexOf('>', i);
      if (tagEnd === -1) {
        result.push(originalInnerHtml.substring(i));
        break;
      }
      result.push(originalInnerHtml.substring(i, tagEnd + 1));
      i = tagEnd + 1;
    } else {
      // Text character — check if it's in the changed region
      if (displayPos === changeStart && !changeInserted) {
        result.push(changedNew);
        changeInserted = true;
      }

      if (displayPos >= changeStart && displayPos < changeEndOriginal) {
        // Inside changed region — skip original character
      } else {
        // Outside changed region — keep original character
        result.push(originalInnerHtml[i]!);
      }

      displayPos++;
      i++;
    }
  }

  // Handle insertion at the very end
  if (!changeInserted) {
    result.push(changedNew);
  }

  return result.join('');
}

/**
 * Extracts editable text segments from email HTML templates.
 * Uses HTML comments as section labels and finds text inside h1-h6 and p tags.
 * Skips segments that contain only template variables or HTML entities.
 *
 * Each segment has:
 * - `content`: original inner HTML (with inline tags) for replacement tracking
 * - `displayText`: stripped plain text for display in inputs
 */
export function extractTextSegments(html: string): TextSegment[] {
  if (!html) return [];

  const comments: Array<{ label: string; index: number }> = [];
  const commentRegex = /<!--\s*(.*?)\s*-->/g;
  let commentMatch;
  while ((commentMatch = commentRegex.exec(html)) !== null) {
    comments.push({ label: commentMatch[1]!, index: commentMatch.index });
  }

  const tagRegex = /<(h[1-6]|p)\b[^>]*>([\s\S]*?)<\/\1>/gi;
  const segments: TextSegment[] = [];
  let id = 0;
  let tagMatch;

  while ((tagMatch = tagRegex.exec(html)) !== null) {
    const tagName = tagMatch[1]!;
    const innerContent = tagMatch[2]!;
    const matchIndex = tagMatch.index;

    const textOnly = stripInlineTags(innerContent);

    // Check if there's meaningful text beyond variables and entities
    const meaningfulText = textOnly
      .replace(/\{\{.*?\}\}/g, '')
      .replace(/&\w+;/g, '')
      .replace(/&#\d+;/g, '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim();

    if (!meaningfulText) continue;

    // Find nearest preceding HTML comment as label
    let label = '';
    for (let i = comments.length - 1; i >= 0; i--) {
      if (comments[i]!.index < matchIndex) {
        label = comments[i]!.label;
        break;
      }
    }

    if (label) {
      label = label
        .split(/[\s_-]+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
    } else {
      label = tagName.match(/^h/i) ? 'Heading' : 'Content';
    }

    segments.push({
      id: id++,
      label,
      content: innerContent.trim(),
      displayText: textOnly,
      tagName: tagName.toLowerCase(),
    });
  }

  return segments;
}

/**
 * Applies edited text segments back into the original HTML.
 *
 * Uses character-level position mapping to surgically replace only the text
 * that changed, preserving all inline HTML tags (strong, em, a, etc.) and
 * their attributes. Unedited segments retain their original HTML intact.
 */
export function applySegmentChanges(
  originalHtml: string,
  originalSegments: TextSegment[],
  editedContents: Record<number, string>,
): string {
  let result = originalHtml;

  // Process in reverse to avoid offset shifts
  const changes = [...originalSegments]
    .filter((s) => editedContents[s.id] !== undefined && editedContents[s.id] !== s.displayText)
    .reverse();

  for (const segment of changes) {
    const editedText = editedContents[segment.id]!;
    const newInnerHtml = rebuildInnerHtml(segment.content, segment.displayText, editedText);
    result = result.replace(segment.content, newInnerHtml);
  }

  return result;
}
